import { fetchSubordinatesLearning, downloadCourseMappingTemplate, uploadCourseMapping, downloadTrainingHistoryTemplate, uploadTrainingHistory } from "../api";
import { ROLE_HSE, ROLE_MANAGER } from "@/constants/mitraRole";
import { mapActions, mapGetters } from "vuex";

const cleanObject = (obj = {}) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
            acc[key] = value;
        }
        return acc;
    }, {});
};

export default {
    data() {
        return {
            isLoading: false,
            searchQuery: "",
            searchTimeout: null,
            isDownloading: false,
            isDownloadingHistoryTemplate: false,
            selectedFile: null,
            selectedHistoryFile: null,
            isUploading: false,
            isUploadingHistory: false,
            filterUsers: {
                company: null,
                division: null,
                department: null,
                position: null,
                employee: null,
            },
            initialFilterOptions: null,
            isSearchingEmployee: false,
            employeeSearchTimeout: null,
            totalRecords: 0,
            totalSubmissions: 0,
            totalPages: 0,
            perPageOptions: [10, 25, 50, 100],
            serverParams: {
                page: 1,
                pageSize: 10,
                order: "desc",
                search: "",
                nik: null,
                all: false,
            },
        };
    },

    computed: {
        ...mapGetters("mytnaReport", ["subordinatesLearning", "filterUserOptions"]),
        ...mapGetters('users', ['profileData']),

        isHROrHSERole() {
          const userRoles = this.profileData?.roles_uenroll || [];
                    return userRoles.some((role) => {
                            const normalizedRole = String(role || "").toLowerCase();
                            return normalizedRole.includes("hr") || normalizedRole === ROLE_HSE;
                    });
        },
        
        isManagerRole() {
          const userRoles = this.profileData?.roles_uenroll || [];
          return userRoles.includes(ROLE_MANAGER);
        },

        displayFilterOptions() {
            return {
                company: ["PT. BORNEO INDOBARA"],
                division: this.initialFilterOptions?.division || this.filterUserOptions.division || [],
                department: this.initialFilterOptions?.department || this.filterUserOptions.department || [],
                position: this.initialFilterOptions?.position || this.filterUserOptions.position || [],
                employee: this.filterUserOptions.employee || [],
            };
        },

        visiblePages() {
            const total = this.totalPages;
            const current = this.serverParams.page;
            if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
            const pages = [];
            pages.push(1);
            if (current > 3) pages.push("...");
            for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
                pages.push(i);
            }
            if (current < total - 2) pages.push("...");
            pages.push(total);
            return pages;
        },

        currentPageSubmissions() {
            return (this.subordinatesLearning.data || []).reduce((sum, item) => sum + (Number(item.total_learning) || 0), 0);
        },

        activeFilterCount() {
            const employeeFilters = ["division", "department", "position", "employee"].filter((key) => {
                const value = this.filterUsers[key];
                return value !== null && value !== undefined && value !== "";
            }).length;
            return employeeFilters + (this.searchQuery.trim() ? 1 : 0);
        },
    },

    methods: {
        ...mapActions("mytnaReport", ["getSubordinatesLearning", "getUserByFilters"]),

        applyUserFilterChange() {
            this.serverParams.nik = null;
            this.serverParams.page = 1;
            this.getUserByFilters(cleanObject({ ...this.filterUsers, company: null, bibOnly: true, employeeLimit: 25 }));
            this.fetchSubordinatesLearning();
        },

        async fetchSubordinatesLearning() {
            this.isLoading = true;

            try {
                const query = cleanObject({
                    ...this.serverParams,
                    ...this.filterUsers,
                    company: null,
                    bibOnly: true,
                    hasLearningOnly: false,
                    approvalOnly: false,
                    countApprovalOnly: true,
                    learning_type: "structured_learning",
                });
                const resSub = await this.getSubordinatesLearning(query);

                this.totalRecords = resSub?.totalItems ?? 0;
                this.totalSubmissions = resSub?.totalLearningItems ?? 0;
                this.totalPages = resSub?.totalPages ?? 0;
                this.serverParams.page = resSub?.page ?? this.serverParams.page;
                this.serverParams.pageSize = resSub?.limit ?? this.serverParams.pageSize;
            } finally {
                this.isLoading = false;
            }
        },

        onPageChange(params) {
            this.serverParams.page = params.currentPage;
            this.fetchSubordinatesLearning();
        },

        onPerPageChange(params) {
            this.serverParams.pageSize = params.currentPerPage;
            this.serverParams.page = 1;
            this.fetchSubordinatesLearning();
        },

        changePage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.serverParams.page = page;
            this.fetchSubordinatesLearning();
        },

        changePerPage() {
            this.serverParams.page = 1;
            this.fetchSubordinatesLearning();
        },

        onSortChange(params) {
            this.serverParams.order = params[0]?.type === "asc" ? "asc" : "desc";
            this.fetchSubordinatesLearning();
        },

        runSearch() {
            this.serverParams.search = this.searchQuery.trim();
            this.serverParams.page = 1;
            this.fetchSubordinatesLearning();
        },

        resetUserFilters() {
            this.filterUsers = {
                company: null,
                division: null,
                department: null,
                position: null,
                employee: null,
            };
            this.searchQuery = "";
            this.serverParams.nik = null;
            this.serverParams.search = "";
            this.serverParams.page = 1;
            this.getUserByFilters({ bibOnly: true, employeeLimit: 25 });
            this.fetchSubordinatesLearning();
        },

        onEmployeeSearch(search, loading) {
            clearTimeout(this.employeeSearchTimeout);
            const term = String(search || "").trim();

            if (term && term.length < 2) return;

            loading?.(true);
            this.isSearchingEmployee = true;
            this.employeeSearchTimeout = setTimeout(async () => {
                try {
                    await this.getUserByFilters(cleanObject({
                        ...this.filterUsers,
                        company: null,
                        search: term,
                        bibOnly: true,
                        employeeLimit: 25,
                    }));
                } finally {
                    this.isSearchingEmployee = false;
                    loading?.(false);
                }
            }, 350);
        },

        async downloadTemplate() {
            if (this.isDownloading) return;

            this.isDownloading = true;
            try {
                const response = await downloadCourseMappingTemplate();

                // Create blob and download
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'TNA_Course_Mapping_Template.xlsx');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.$swal({
                    title: 'Success',
                    text: 'Template berhasil diunduh',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#2c7be5'
                });
            } catch (error) {
                console.error('Error downloading template:', error);
                this.$swal({
                    title: 'Error',
                    text: 'Template belum berhasil diunduh. Silakan coba lagi.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#e63757'
                });
            } finally {
                this.isDownloading = false;
            }
        },

        showBulkUploadModal() {
            this.selectedFile = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
            this.$bvModal.show('modal-bulk-upload-course-mapping');
        },

        showTrainingHistoryUploadModal() {
            this.selectedHistoryFile = null;
            if (this.$refs.historyFileInput) {
                this.$refs.historyFileInput.value = '';
            }
            this.$bvModal.show('modal-bulk-upload-training-history');
        },

        onFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                // Validate file size (5MB max)
                if (file.size > 5 * 1024 * 1024) {
                    this.$swal({
                        title: 'Error',
                        text: 'Ukuran file harus kurang dari 5MB',
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                    event.target.value = '';
                    return;
                }

                // Validate file type
                if (!file.name.toLowerCase().endsWith('.xlsx')) {
                    this.$swal({
                        title: 'Error',
                        text: 'Hanya file .xlsx yang diizinkan',
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                    event.target.value = '';
                    return;
                }

                this.selectedFile = file;
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },

        onHistoryFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                if (file.size > 5 * 1024 * 1024) {
                    this.$swal({
                        title: 'Error',
                        text: 'Ukuran file harus kurang dari 5MB',
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                    event.target.value = '';
                    return;
                }

                if (!file.name.toLowerCase().endsWith('.xlsx')) {
                    this.$swal({
                        title: 'Error',
                        text: 'Hanya file .xlsx yang diizinkan',
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                    event.target.value = '';
                    return;
                }

                this.selectedHistoryFile = file;
            }
        },

        async downloadHistoryTemplate() {
            if (this.isDownloadingHistoryTemplate) return;

            this.isDownloadingHistoryTemplate = true;
            try {
                const response = await downloadTrainingHistoryTemplate();
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'Training_History_Template.xlsx');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.$swal({
                    title: 'Success',
                    text: 'Template riwayat pelatihan berhasil diunduh',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#2c7be5'
                });
            } catch (error) {
                console.error('Error downloading training history template:', error);
                this.$swal({
                    title: 'Error',
                    text: 'Template riwayat pelatihan belum berhasil diunduh. Silakan coba lagi.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#e63757'
                });
            } finally {
                this.isDownloadingHistoryTemplate = false;
            }
        },

        async uploadHistoryFile() {
            if (!this.selectedHistoryFile) {
                this.$swal({
                    title: 'Error',
                    text: 'Pilih file yang akan diunggah',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                return;
            }

            this.isUploadingHistory = true;

            try {
                const formData = new FormData();
                formData.append('file', this.selectedHistoryFile);
                const response = await uploadTrainingHistory(formData);
                const payload = response.data?.data || response.data || {};
                const uploadedData = payload.uploadedData || [];
                const failedRecords = payload.failedRecords || [];
                const successCount = Math.max(uploadedData.length - failedRecords.length, 0);

                let resultHtml = `
                    <div class="text-left">
                        <p><strong>Upload Summary:</strong></p>
                        <ul>
                            <li>Completed History Records: ${successCount}</li>
                            <li>Failed Records: ${failedRecords.length}</li>
                        </ul>
                    </div>
                `;

                if (failedRecords.length > 0) {
                    resultHtml += `
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th>Row</th>
                                        <th>NIK</th>
                                        <th>Course ID</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                    `;
                    failedRecords.forEach(record => {
                        resultHtml += `
                            <tr>
                                <td>${record.rowNumber || '-'}</td>
                                <td>${record.nik || '-'}</td>
                                <td>${record.training_id || '-'}</td>
                                <td>${record.error || '-'}</td>
                            </tr>
                        `;
                    });
                    resultHtml += `
                                </tbody>
                            </table>
                        </div>
                    `;
                }

                await this.$swal({
                    title: failedRecords.length > 0 ? 'Unggah Selesai dengan Error' : 'Unggah Berhasil',
                    html: resultHtml,
                    icon: failedRecords.length > 0 ? 'warning' : 'success',
                    confirmButtonText: 'OK',
                    width: '700px'
                });

                this.$bvModal.hide('modal-bulk-upload-training-history');
                this.selectedHistoryFile = null;
                this.fetchSubordinatesLearning();
            } catch (error) {
                console.error('Training history upload error:', error);
                this.$swal({
                    title: 'Error',
                    text: error.response?.data?.message || 'File riwayat pelatihan belum berhasil diunggah. Silakan coba lagi.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            } finally {
                this.isUploadingHistory = false;
            }
        },

        async uploadFile() {
            if (!this.selectedFile) {
                this.$swal({
                    title: 'Error',
                    text: 'Pilih file yang akan diunggah',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                return;
            }

            this.isUploading = true;

            try {
                const formData = new FormData();
                formData.append('file', this.selectedFile);

                const response = await uploadCourseMapping(formData);

                // Show success message
                const data = response.data || {};
                const summary = data.summary || data.data || {};

                let successMessage = `
                    <div class="text-left">
                        <p><strong>Unggah Berhasil!</strong></p>
                `;

                if (summary.processed !== undefined || summary.success !== undefined) {
                    successMessage += `
                        <ul class="list-unstyled mb-0">
                            ${summary.processed ? `<li>✓ Records Processed: ${summary.processed}</li>` : ''}
                            ${summary.success ? `<li>✓ Successfully Updated: ${summary.success}</li>` : ''}
                            ${summary.failed ? `<li>⚠ Failed: ${summary.failed}</li>` : ''}
                        </ul>
                    `;
                }

                successMessage += `</div>`;

                await this.$swal({
                    title: 'Success',
                    html: successMessage,
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

                // Close modal and refresh data
                this.$bvModal.hide('modal-bulk-upload-course-mapping');
                this.selectedFile = null;
                this.fetchSubordinatesLearning();

            } catch (error) {
                console.error('Upload error:', error);

                let errorMessage = 'File belum berhasil diunggah. Silakan coba lagi.';

                if (error.response && error.response.data) {
                    const errorData = error.response.data;
                    if (errorData.message) {
                        errorMessage = errorData.message;
                    }
                }

                this.$swal({
                    title: 'Error',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            } finally {
                this.isUploading = false;
            }
        },
    },

    watch: {
        "filterUsers.company"() {
            this.filterUsers.company = null;
        },

        "filterUsers.division"() {
            this.applyUserFilterChange();
        },

        "filterUsers.department"() {
            this.applyUserFilterChange();
        },

        "filterUsers.position"() {
            this.applyUserFilterChange();
        },

        "filterUsers.employee"(newVal) {
            this.serverParams.nik = newVal || null;
            this.serverParams.page = 1;
            this.fetchSubordinatesLearning();
        },

        searchQuery() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.runSearch();
            }, 500);
        },
    },

    async mounted() {
        await Promise.all([
            this.fetchSubordinatesLearning(),
            this.getUserByFilters({ bibOnly: true, employeeLimit: 25 }),
        ]);

        this.initialFilterOptions = {
            company: ["PT. BORNEO INDOBARA"],
            division: [...(this.filterUserOptions.division || [])],
            department: [...(this.filterUserOptions.department || [])],
            position: [...(this.filterUserOptions.position || [])],
        };
    },

    beforeDestroy() {
        clearTimeout(this.searchTimeout);
        clearTimeout(this.employeeSearchTimeout);
    },
};
